import { FC, useState, useEffect } from 'react';
import { Minus, Plus, Trash2, Eye, FileText } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { CartItemData, useUpdateCartItem, useRemoveCartItem } from '../../../hooks/useCart';
import { getLocalizedField } from '../../../utils/localization';

interface CartItemProps {
    item: CartItemData;
}

export const CartItem: FC<CartItemProps> = ({ item }) => {
    const { t, i18n } = useTranslation('shop');
    const { mutate: updateQuantity, isPending: isUpdating } = useUpdateCartItem();
    const { mutate: removeItem, isPending: isRemoving } = useRemoveCartItem();

    // Local state for optimistic UI updates during debounce
    const [localQty, setLocalQty] = useState(item.quantity);

    // Sync local state if external state changes
    useEffect(() => {
        setLocalQty(item.quantity);
    }, [item.quantity]);

    const handleIncrement = () => {
        const productStock = item.variant ? item.variant.stock : item.product.stock;
        if (localQty < productStock) {
            const newQty = localQty + 1;
            setLocalQty(newQty);
            updateQuantity({ itemId: item.id, quantity: newQty });
        }
    };

    const handleDecrement = () => {
        if (localQty > 1) {
            const newQty = localQty - 1;
            setLocalQty(newQty);
            updateQuantity({ itemId: item.id, quantity: newQty });
        }
    };

    const handleRemove = () => {
        removeItem(item.id);
    };

    const unitPrice = item.variant?.price ?? item.product.price;
    const lensUnitPrice = item.lensPrice || 0;
    const combinedUnitPrice = unitPrice + lensUnitPrice;
    const lineTotal = combinedUnitPrice * localQty;
    const maxStock = item.variant ? item.variant.stock : item.product.stock;

    return (
        <div className={`flex gap-4 py-4 border-b border-gray-100 transition-opacity ${isRemoving ? 'opacity-50 pointer-events-none' : 'opacity-100'}`}>
            {/* Image Thumbnail */}
            <div className="w-20 h-20 lg:w-24 lg:h-24 rounded-xl overflow-hidden bg-gray-50 flex-shrink-0 border border-gray-100">
                {item.product.images?.[0]?.url ? (
                    <img
                        src={`${(import.meta as any).env.VITE_API_BASE_URL?.replace('/api', '') || ''}${item.product.images[0].url}`}
                        alt={item.product.name}
                        className="w-full h-full object-cover"
                    />
                ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-300">
                        No img
                    </div>
                )}
            </div>

            {/* Details */}
            <div className="flex-1 flex flex-col min-w-0">
                <div className="flex justify-between items-start gap-2">
                    <h4 className="font-bold text-gray-900 text-sm lg:text-base line-clamp-2">
                        {getLocalizedField(item.product, 'name', i18n.language)}
                    </h4>
                    <button
                        onClick={handleRemove}
                        disabled={isRemoving}
                        className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
                        aria-label={t('cart.remove')}
                    >
                        <Trash2 size={18} />
                    </button>
                </div>

                {/* Variant Info */}
                {item.variant && (
                    <div className="text-sm text-gray-500 mt-0.5">
                        {getLocalizedField(item.variant, 'name', i18n.language) || item.variant.name}
                    </div>
                )}

                {/* Rx Ready Warning Pill */}
                {item.product.isPrescriptionReady && !item.product.isReadyReadingGlasses && !item.lensVariantId && !item.frameOnly && (
                    <div className="mt-1.5 inline-flex items-center gap-1.5 w-max px-2 py-0.5 rounded bg-blue-50 text-blue-700 text-xs font-medium border border-blue-100">
                        <div className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse"></div>
                        {String(t('cart.lensNotConfigured', 'Lenses not configured'))}
                    </div>
                )}

                {/* Lens & Prescription Info */}
                {(item.lensVariant || item.prescriptionData?.entryMethod === 'READING_GLASSES' || item.prescriptionData?.entryMethod?.startsWith('CONTACT_LENS')) && (
                    <div className="mt-2 p-2.5 bg-blue-50/50 rounded-lg border border-blue-100/50">
                        {item.lensVariant && (
                            <p className="text-xs font-semibold text-blue-900 flex items-center gap-1.5 mb-1.5">
                                <Eye className="w-3.5 h-3.5" />
                                {item.lensVariant.lensProduct?.translations?.[0]?.name || item.lensVariant.lensProduct?.feature} - {item.lensVariant.tier} {item.lensVariant.refractiveIndex}
                                {item.lensPrice ? (
                                    <span className="text-blue-500 font-mono text-[10px] ml-1 tracking-wider">(+{item.lensPrice.toLocaleString()} EGP)</span>
                                ) : null}
                            </p>
                        )}
                        {item.prescriptionData && (
                            <div className="space-y-1">
                                {item.prescriptionData.entryMethod === 'READING_GLASSES' ? (
                                    <div className="text-[12px] text-emerald-800 font-bold flex items-center gap-1.5 bg-emerald-50 w-max px-2 py-1 rounded border border-emerald-200 mb-1.5">
                                        <Eye className="w-3.5 h-3.5" />
                                        {String(t('cart.readingPower', 'Reading Power'))}: {item.prescriptionData.addPower > 0 ? '+' : ''}{Number(item.prescriptionData.addPower).toFixed(2)}
                                    </div>
                                ) : item.prescriptionData.entryMethod === 'CONTACT_LENS_NO_RX' ? (
                                    <div className="text-[12px] text-cyan-800 font-bold flex items-center gap-1.5 bg-cyan-50 w-max px-2 py-1 rounded border border-cyan-200 mb-1.5">
                                        <Eye className="w-3.5 h-3.5" />
                                        {String(t('cart.contactLensNoRx', 'Contact Lenses (No Rx)'))}
                                    </div>
                                ) : item.prescriptionData.entryMethod === 'CONTACT_LENS_RX' ? (
                                    <>
                                        <div className="text-[12px] text-cyan-800 font-bold flex items-center gap-1.5 bg-cyan-50 w-max px-2 py-1 rounded border border-cyan-200 mb-1.5">
                                            <Eye className="w-3.5 h-3.5" />
                                            {String(t('cart.contactLensWithRx', 'Contact Lenses (With Rx)'))}
                                        </div>
                                        {item.prescriptionData.rightEye && (
                                            <div className="text-[10px] text-cyan-800/80 flex gap-2">
                                                <span className="font-bold w-4">OD:</span>
                                                <span>SPH: {item.prescriptionData.rightEye.sphere}</span>
                                                <span>CYL: {item.prescriptionData.rightEye.cylinder}</span>
                                                <span>AXIS: {item.prescriptionData.rightEye.axis}</span>
                                            </div>
                                        )}
                                        {item.prescriptionData.leftEye && (
                                            <div className="text-[10px] text-cyan-800/80 flex gap-2">
                                                <span className="font-bold w-4">OS:</span>
                                                <span>SPH: {item.prescriptionData.leftEye.sphere}</span>
                                                <span>CYL: {item.prescriptionData.leftEye.cylinder}</span>
                                                <span>AXIS: {item.prescriptionData.leftEye.axis}</span>
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        {item.prescriptionData.prescriptionImageUrl && (
                                            <div className="text-[10px] text-blue-800 font-bold flex items-center gap-1.5 bg-white/50 w-max px-2 py-1 rounded-md mb-1.5">
                                                <FileText className="w-3.5 h-3.5" />
                                                {t('prescription.uploadedBadge', 'Prescription Uploaded')}
                                            </div>
                                        )}
                                        
                                        {(!item.prescriptionData.entryMethod || item.prescriptionData.entryMethod === 'MANUAL') && (
                                            <>
                                                {item.prescriptionData.rightEye && (
                                                    <div className="text-[10px] text-blue-800/80 flex gap-2">
                                                        <span className="font-bold w-4">OD:</span>
                                                        <span>SPH: {item.prescriptionData.rightEye.sphere}</span>
                                                        <span>CYL: {item.prescriptionData.rightEye.cylinder}</span>
                                                        <span>AXIS: {item.prescriptionData.rightEye.axis}</span>
                                                    </div>
                                                )}
                                                {item.prescriptionData.leftEye && (
                                                    <div className="text-[10px] text-blue-800/80 flex gap-2">
                                                        <span className="font-bold w-4">OS:</span>
                                                        <span>SPH: {item.prescriptionData.leftEye.sphere}</span>
                                                        <span>CYL: {item.prescriptionData.leftEye.cylinder}</span>
                                                        <span>AXIS: {item.prescriptionData.leftEye.axis}</span>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                        
                                        {(item.prescriptionData.pd || item.prescriptionData.addPower) && (
                                            <div className="text-[10px] text-blue-800/80 flex gap-4 pt-0.5">
                                                {item.prescriptionData.pd && (
                                                    <div className="flex gap-2">
                                                        <span className="font-bold">PD:</span>
                                                        <span>{item.prescriptionData.pd}</span>
                                                    </div>
                                                )}
                                                {item.prescriptionData.addPower && (
                                                    <div className="flex gap-2">
                                                        <span className="font-bold">ADD:</span>
                                                        <span>+{item.prescriptionData.addPower.toFixed(2)}</span>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* Pricing & Controls Row */}
                <div className="mt-auto pt-3 flex items-end justify-between">
                    {/* Stepper */}
                    <div className="flex items-center">
                        <div className="flex items-center bg-gray-50 border border-gray-200 rounded-lg h-9">
                            <button
                                onClick={handleDecrement}
                                disabled={localQty <= 1 || isUpdating}
                                className="w-8 h-full flex items-center justify-center text-gray-600 hover:bg-gray-200 hover:text-gray-900 disabled:opacity-50 disabled:hover:bg-transparent rounded-l-lg transition-colors"
                            >
                                <Minus size={14} strokeWidth={2.5} />
                            </button>
                            <span className="w-8 text-center text-sm font-bold text-gray-900">
                                {localQty}
                            </span>
                            <button
                                onClick={handleIncrement}
                                disabled={localQty >= maxStock || isUpdating}
                                className="w-8 h-full flex items-center justify-center text-gray-600 hover:bg-gray-200 hover:text-gray-900 disabled:opacity-50 disabled:hover:bg-transparent rounded-r-lg transition-colors"
                            >
                                <Plus size={14} strokeWidth={2.5} />
                            </button>
                        </div>
                        {localQty >= maxStock && (
                            <span className="text-xs text-orange-500 ml-3 font-medium hidden sm:inline">
                                Max stock ({maxStock})
                            </span>
                        )}
                    </div>

                    {/* Price */}
                    <div className="text-right">
                        {localQty > 1 && (
                            <div className="text-xs text-gray-500 mb-0.5">
                                EGP {combinedUnitPrice.toLocaleString()} each
                            </div>
                        )}
                        <div className="font-bold text-gray-900 text-base lg:text-lg">
                            EGP {lineTotal.toLocaleString()}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
